import { useFloorPlanStore } from "@/stores/floorPlan";
import type { KitchenZone } from "@byk/shared";

const ZONES: { id: KitchenZone; label: string; color: string }[] = [
  { id: "prep", label: "Preparation", color: "bg-green-100 text-green-700" },
  { id: "cooking", label: "Cooking", color: "bg-orange-100 text-orange-700" },
  { id: "cleaning", label: "Cleaning", color: "bg-blue-100 text-blue-700" },
  { id: "storage", label: "Storage", color: "bg-purple-100 text-purple-700" },
];

export const PropertiesPanel: React.FC = () => {
  const {
    selectedId,
    volumes,
    walls,
    elements,
    room,
  } = useFloorPlanStore();

  const {
    updateVolume,
    rotateVolume,
    removeVolume,
    updateWall,
    removeWall,
    removeElement,
    getProductById,
  } = useFloorPlanStore();

  const selectedVolume = volumes.find((v) => v.id === selectedId);
  const selectedWall = walls.find((w) => w.id === selectedId);
  const selectedElement = elements.find((e) => e.id === selectedId);

  const selectedProduct = selectedVolume
    ? getProductById(selectedVolume.productId)
    : null;

  // No selection
  if (!selectedId) {
    return (
      <div className="flex h-full flex-col">
        <div className="border-b p-4">
          <h2 className="text-lg font-semibold text-gray-900">Properties</h2>
        </div>
        <div className="flex flex-1 items-center justify-center p-4">
          <div className="text-center text-gray-400">
            <svg
              className="mx-auto mb-2 h-12 w-12"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
              />
            </svg>
            <p className="text-sm">Select an object to view its properties</p>
          </div>
        </div>

        {/* Room summary */}
        <div className="border-t p-4">
          <h3 className="mb-3 text-sm font-medium text-gray-700">Room Summary</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Dimensions</span>
              <span className="font-medium">
                {room.width} x {room.depth} cm
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Ceiling Height</span>
              <span className="font-medium">{room.height} cm</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Walls</span>
              <span className="font-medium">{walls.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Products</span>
              <span className="font-medium">{volumes.length}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Selected volume (product)
  if (selectedVolume && selectedProduct) {
    return (
      <div className="flex h-full flex-col">
        <div className="border-b p-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {selectedProduct.name}
          </h2>
          <p className="text-sm text-gray-500">{selectedProduct.sku}</p>
        </div>

        <div className="flex-1 overflow-y-auto p-4" style={{ overscrollBehaviorY: 'contain' }}>
          {/* Product info */}
          <div className="mb-6">
            <h3 className="mb-2 text-sm font-medium text-gray-700">Product</h3>
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="mb-2 text-sm text-gray-600">
                {selectedProduct.description}
              </div>
              {selectedProduct.dimensions && (
                <div className="text-xs text-gray-500">
                  {selectedProduct.dimensions.width} x{" "}
                  {selectedProduct.dimensions.depth} x{" "}
                  {selectedProduct.dimensions.height} cm
                </div>
              )}
              <div className="mt-2 text-sm font-medium text-blue-600">
                {selectedProduct.price?.toLocaleString()} {selectedProduct.currency}
              </div>
            </div>
          </div>

          {/* Position */}
          <div className="mb-6">
            <h3 className="mb-2 text-sm font-medium text-gray-700">Position</h3>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="mb-1 block text-xs text-gray-500">X</label>
                <input
                  type="number"
                  value={Math.round(selectedVolume.position.x)}
                  onChange={(e) =>
                    updateVolume(selectedId, {
                      position: {
                        ...selectedVolume.position,
                        x: Number(e.target.value),
                      },
                    })
                  }
                  className="w-full rounded border px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-gray-500">Y</label>
                <input
                  type="number"
                  value={Math.round(selectedVolume.position.y)}
                  onChange={(e) =>
                    updateVolume(selectedId, {
                      position: {
                        ...selectedVolume.position,
                        y: Number(e.target.value),
                      },
                    })
                  }
                  className="w-full rounded border px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-gray-500">Z</label>
                <input
                  type="number"
                  value={Math.round(selectedVolume.position.z)}
                  onChange={(e) =>
                    updateVolume(selectedId, {
                      position: {
                        ...selectedVolume.position,
                        z: Number(e.target.value),
                      },
                    })
                  }
                  className="w-full rounded border px-2 py-1 text-sm"
                />
              </div>
            </div>
          </div>

          {/* Rotation */}
          <div className="mb-6">
            <h3 className="mb-2 text-sm font-medium text-gray-700">Rotation</h3>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="0"
                max="360"
                step="15"
                value={selectedVolume.rotation}
                onChange={(e) => rotateVolume(selectedId, Number(e.target.value))}
                className="flex-1"
              />
              <span className="w-12 text-right text-sm text-gray-600">
                {selectedVolume.rotation}°
              </span>
            </div>
            <div className="mt-2 flex gap-1">
              {[0, 90, 180, 270].map((angle) => (
                <button
                  key={angle}
                  onClick={() => rotateVolume(selectedId, angle)}
                  className="flex-1 rounded bg-gray-100 py-1 text-xs font-medium text-gray-600 hover:bg-gray-200"
                >
                  {angle}°
                </button>
              ))}
            </div>
          </div>

          {/* Zone */}
          <div className="mb-6">
            <h3 className="mb-2 text-sm font-medium text-gray-700">
              Kitchen Zone
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {ZONES.map((zone) => (
                <button
                  key={zone.id}
                  onClick={() => updateVolume(selectedId, { zone: zone.id })}
                  className={`rounded-lg px-3 py-2 text-xs font-medium transition-colors ${
                    selectedVolume.zone === zone.id
                      ? zone.color
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {zone.label}
                </button>
              ))}
            </div>
          </div>

          {/* Kosher distinction */}
          <div className="mb-6">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={selectedVolume.isKashrutDistinct || false}
                onChange={(e) =>
                  updateVolume(selectedId, {
                    isKashrutDistinct: e.target.checked,
                  })
                }
                className="h-4 w-4 rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">
                Kashrut distinct (separate meat/dairy)
              </span>
            </label>
          </div>
        </div>

        {/* Delete button */}
        <div className="border-t p-4">
          <button
            onClick={() => removeVolume(selectedId)}
            className="w-full rounded-lg bg-red-50 py-2 text-sm font-medium text-red-600 hover:bg-red-100"
          >
            Remove Product
          </button>
        </div>
      </div>
    );
  }

  // Selected wall
  if (selectedWall) {
    const wallLength = Math.hypot(
      selectedWall.end.x - selectedWall.start.x,
      selectedWall.end.y - selectedWall.start.y
    );

    return (
      <div className="flex h-full flex-col">
        <div className="border-b p-4">
          <h2 className="text-lg font-semibold text-gray-900">Wall</h2>
          <p className="text-sm text-gray-500">
            {Math.round(wallLength)} cm long
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-4" style={{ overscrollBehaviorY: 'contain' }}>
          {/* Thickness */}
          <div className="mb-6">
            <h3 className="mb-2 text-sm font-medium text-gray-700">Thickness</h3>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={selectedWall.thickness}
                onChange={(e) =>
                  updateWall(selectedId, { thickness: Number(e.target.value) })
                }
                className="w-20 rounded border px-2 py-1 text-sm"
                min={5}
                max={50}
                step={5}
              />
              <span className="text-sm text-gray-500">cm</span>
            </div>
          </div>

          {/* Height */}
          <div className="mb-6">
            <h3 className="mb-2 text-sm font-medium text-gray-700">Height</h3>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={selectedWall.height}
                onChange={(e) =>
                  updateWall(selectedId, { height: Number(e.target.value) })
                }
                className="w-20 rounded border px-2 py-1 text-sm"
                min={100}
                max={400}
                step={10}
              />
              <span className="text-sm text-gray-500">cm</span>
            </div>
          </div>

          {/* Coordinates */}
          <div className="mb-6">
            <h3 className="mb-2 text-sm font-medium text-gray-700">
              Coordinates
            </h3>
            <div className="rounded-lg bg-gray-50 p-3 text-xs text-gray-600">
              <div>
                Start: ({Math.round(selectedWall.start.x)},{" "}
                {Math.round(selectedWall.start.y)})
              </div>
              <div>
                End: ({Math.round(selectedWall.end.x)},{" "}
                {Math.round(selectedWall.end.y)})
              </div>
            </div>
          </div>
        </div>

        {/* Delete button */}
        <div className="border-t p-4">
          <button
            onClick={() => removeWall(selectedId)}
            className="w-full rounded-lg bg-red-50 py-2 text-sm font-medium text-red-600 hover:bg-red-100"
          >
            Remove Wall
          </button>
        </div>
      </div>
    );
  }

  // Selected element (window/door)
  if (selectedElement) {
    return (
      <div className="flex h-full flex-col">
        <div className="border-b p-4">
          <h2 className="text-lg font-semibold capitalize text-gray-900">
            {selectedElement.type.replace("_", " ")}
          </h2>
        </div>

        <div className="flex-1 overflow-y-auto p-4" style={{ overscrollBehaviorY: 'contain' }}>
          <div className="mb-6">
            <h3 className="mb-2 text-sm font-medium text-gray-700">
              Dimensions
            </h3>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="mb-1 block text-xs text-gray-500">Width</label>
                <input
                  type="number"
                  value={selectedElement.width}
                  className="w-full rounded border px-2 py-1 text-sm"
                  readOnly
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-gray-500">Height</label>
                <input
                  type="number"
                  value={selectedElement.height}
                  className="w-full rounded border px-2 py-1 text-sm"
                  readOnly
                />
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="mb-2 text-sm font-medium text-gray-700">Elevation</h3>
            <div className="text-sm text-gray-600">
              {selectedElement.elevation} cm from floor
            </div>
          </div>
        </div>

        <div className="border-t p-4">
          <button
            onClick={() => removeElement(selectedId)}
            className="w-full rounded-lg bg-red-50 py-2 text-sm font-medium text-red-600 hover:bg-red-100"
          >
            Remove {selectedElement.type}
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default PropertiesPanel;
